import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../services/apiClient';
import { useAuth } from './AuthContext';
import { BillingContext } from './billingContextInstance';
import { RENEWAL_PAID_EVENT } from '../components/billing/renewalNotice';

// El objeto de contexto y sus lectores viven en `billingContextInstance.js`: este
// fichero arrastra AuthContext (y con él supabase e i18n), y un componente que
// sólo quiere LEER el estado no debe cargar con eso. Ver la cabecera de ese
// fichero — se llevó por delante 34 tests antes de separarse.

export const BillingProvider = ({ children }) => {
  // null | umbral de aviso alcanzado (50, 80…: los da el backend) | 100 (plan agotado).
  const [warningLevel, setWarningLevel] = useState(null);
  const [usage, setUsage] = useState(null); // full /api/billing/usage response
  // Marca de tiempo de la última vez que se pidió abrir el aviso del 100: al ENTRAR en el plan
  // agotado, o cuando el usuario lo pide (`openLimitNotice`). Seguir chocando con el tope no lo
  // reabre: cada corte ya trae su aviso con la misma salida (`CreditStopNotice`, #1294). Antes lo
  // reabría cada señal (#1299, cuando no había otro aviso), y un solo 402 sacaba el modal, el aviso
  // del corte y el error de la pantalla a la vez.
  const [blockSignalAt, setBlockSignalAt] = useState(0);
  // #1294 — el último corte por saldo: { reason, surface?, docsUsed?, docsMax?, at }. Lo pinta
  // `CreditStopNotice`, el aviso ÚNICO para todas las superficies (chat, planes, vectorización).
  const [stopNotice, setStopNotice] = useState(null);
  // #759: cuándo cortó el BACKEND por saldo en esta sesión. No es lo mismo que `warningLevel`
  // 100: ese sale del % de /api/billing/usage, que va inflado un 10 % para el display y llega
  // antes que el corte real. El modal de una prueba o un comp solo deja de cerrarse con esto.
  const [serverBlockedAt, setServerBlockedAt] = useState(0);
  const { user, userProfile } = useAuth();

  // El nivel de ahora, para saber si una señal ENTRA en el 100 o ya estaba ahí. Se lee y se escribe
  // aquí mismo: dos señales en el mismo instante no esperan a que React pinte.
  const levelRef = useRef(null);
  useEffect(() => { levelRef.current = warningLevel; }, [warningLevel]);

  const escalate = (level) => {
    const prev = levelRef.current;
    if (level >= 100 && prev !== 100) setBlockSignalAt(Date.now());
    if (prev === null || level > prev) levelRef.current = level;
    setWarningLevel(current => (current === null || level > current) ? level : current);
  };

  // Proactive: check usage on login so the banner shows before the first message.
  // Wait until terms are accepted — the backend gates /api/billing/usage behind
  // requireTermsAccepted, so polling before acceptance just produces 451s in the
  // network log. The banner has nothing to show on /onboarding/terms anyway.
  // `{ reset: true }` (#759 C): tras cambiar de plan el consumo baja, y los avisos tienen que bajar con
  // él. Sin reset solo suben: un aviso cerrado no vuelve por una lectura que no ha cambiado nada.
  const refreshUsage = useCallback((opts) => {
    const reset = opts?.reset === true;
    if (!user) return;
    if (!userProfile?.terms_accepted_at) return;
    apiClient.get('/api/billing/usage')
      .then(r => {
        if (!r) return;
        // La exención tiene que ser LA MISMA que la del backend, y el backend
        // exime sólo a `dev_internal` (equipo Flock, con credenciales propias):
        // ver `checkSubscriptionLimits`, que dejó de eximir a `code_redeemed`
        // el 2026-06-18 (billing-custom-budget-enforcement).
        //
        // Aquí seguía exento, así que a un cliente con código el backend le
        // bloqueaba al 100 % mientras esta consulta decidía que no había nada
        // que avisar: ni banner, ni triángulo, ni modal al recargar. Justo el
        // perfil de la cuenta que se quedó sin crédito el 2026-09-21.
        //
        // ⚠️ Si mañana cambia la exención del backend, cambia también aquí: dos
        // listas que deciden lo mismo por separado vuelven a separarse.
        if (r.source === 'dev_internal') return;
        setUsage(r);
        // Los umbrales los decide el backend (#759 D18: 50 y 80 en prueba y comp); aquí solo se
        // mira cuál se ha alcanzado.
        const warnThresholds = (r.warningThresholdsPct ?? [80]).map(Number);
        const blockThr = Number(r.blockThresholdPct ?? 100);
        const pct = Number(r.percentUsed ?? 0);
        const reached = warnThresholds.filter((thr) => pct >= thr);
        if (reset) {
          setWarningLevel(pct >= blockThr ? 100 : (reached.length > 0 ? Math.max(...reached) : null));
          return;
        }
        if (pct >= blockThr) escalate(100);
        else if (reached.length > 0) escalate(Math.max(...reached));
      })
      .catch(() => {}); // fail open — don't block on aggregator errors
  }, [user, userProfile?.terms_accepted_at]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refreshUsage(); }, [refreshUsage]);

  // #1294 F6: un corte por saldo es justo cuando el consumo ha cambiado. Antes solo se leía al
  // montar, y la barra seguía diciendo lo de hace minutos después de que algo se parara.
  useEffect(() => {
    const handler = (e) => {
      setStopNotice(e.detail);
      setServerBlockedAt(Date.now());
      refreshUsage();
    };
    window.addEventListener('billing:stop', handler);
    return () => window.removeEventListener('billing:stop', handler);
  }, [refreshUsage]);

  // #759: la prueba o el comp vencen a mitad de sesión (402 trial_expired). La página de planes
  // lee `freeAccess` de aquí: sin refrescar, enseñaría el estado de antes del corte.
  useEffect(() => {
    window.addEventListener('billing:trial-expired', refreshUsage);
    return () => window.removeEventListener('billing:trial-expired', refreshUsage);
  }, [refreshUsage]);

  // #759 R: la renovación sin cobrar ya está pagada (lo ve /billing/renewal). Sin refrescar, el
  // aviso de la app seguiría leyendo `past_due`.
  useEffect(() => {
    window.addEventListener(RENEWAL_PAID_EVENT, refreshUsage);
    return () => window.removeEventListener(RENEWAL_PAID_EVENT, refreshUsage);
  }, [refreshUsage]);

  // Reactive: events dispatched by apiClient on X-Usage-Warning header or 402
  useEffect(() => {
    const handler = (e) => escalate(e.detail.level);
    window.addEventListener('billing:usage', handler);
    return () => window.removeEventListener('billing:usage', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissWarning = () => {
    setWarningLevel(prev => (prev !== null && prev < 100) ? null : prev);
  };

  /**
   * Vuelve a abrir el aviso de plan agotado a petición del usuario.
   *
   * Reutiliza `blockSignalAt` en vez de añadir un segundo interruptor: el modal
   * ya se reabre cuando esa marca cambia, así que «he chocado otra vez con el
   * tope» y «quiero volver a ver el aviso» son el mismo gesto para él.
   *
   * Existe para que pedir más uso NO dependa de que el modal salte solo: hasta
   * ahora, cerrarlo dejaba al usuario sin ninguna otra puerta (#1300).
   */
  const openLimitNotice = () => setBlockSignalAt(Date.now());

  // El plan está agotado. Se deriva de `warningLevel` y no de `usage` porque un
  // 402 del backend es una fuente válida aunque la consulta de consumo aún no
  // haya refrescado: el agregador tarda hasta 5 min.
  const isExhausted = warningLevel === 100;

  return (
    <BillingContext.Provider value={{
      warningLevel,
      dismissWarning,
      usage,
      blockSignalAt,
      serverBlockedAt,
      openLimitNotice,
      isExhausted,
      stopNotice,
      dismissStopNotice: () => setStopNotice(null),
      isTrial: usage?.source === 'trial',
      // #759 C: tras cambiar de plan, el consumo y lo que se ofrece al agotar cambian.
      refreshUsage,
    }}>
      {children}
    </BillingContext.Provider>
  );
};

export { useBilling, useBillingOptional } from './billingContextInstance';
